import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Radio, Target, Upload, DollarSign, BarChart3, Users, ChevronRight,
  TrendingUp, Film, Eye, Clock, LogOut, User, Plus, ArrowUpRight, Zap,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const map = { pending:'bg-gray-500/10 text-gray-400 border-gray-500/20', approved:'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', rejected:'bg-red-500/10 text-red-500 border-red-500/20', active:'bg-blue-500/10 text-blue-400 border-blue-500/20', completed:'bg-gray-500/10 text-gray-400 border-gray-500/20' };
  return <Badge className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider font-mono border ${map[status]||map.pending}`}>{status}</Badge>;
}

export default function BrandHome() {
  const { user, token, logout, API } = useAuth();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [streamers, setStreamers] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [adRes, campRes, placeRes, strRes] = await Promise.all([
        axios.get(`${API}/ads/my`, { headers }),
        axios.get(`${API}/campaigns/my`, { headers }),
        axios.get(`${API}/placements/my`, { headers }),
        axios.get(`${API}/streamer-profiles`, { headers }).catch(() => ({ data: [] })),
      ]);
      setAds(adRes.data);
      setCampaigns(campRes.data);
      setPlacements(placeRes.data);
      setStreamers(strRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totalSpend = placements.reduce((s, p) => s + (p.spend || 0), 0);
  const totalImpressions = placements.reduce((s, p) => s + (p.impressions || 0), 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const approvedAds = ads.filter(a => a.status === 'approved').length;
  const pendingPlacements = placements.filter(p => p.status === 'pending').length;
  const approvedPlacements = placements.filter(p => p.status === 'approved').length;
  const fullUrl = (url) => url?.startsWith('http') ? url : `${BACKEND_URL}${url}`;

  // Onboarding checklist
  const steps = [
    { label: 'Upload an ad creative', done: ads.length > 0 },
    { label: 'Get an ad approved', done: approvedAds > 0 },
    { label: 'Create a campaign', done: campaigns.length > 0 },
    { label: 'Place your first bid', done: placements.length > 0 },
    { label: 'Get a placement approved', done: approvedPlacements > 0 },
  ];
  const completedSteps = steps.filter(s => s.done).length;
  const onboardingComplete = completedSteps === steps.length;

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#7C3AED] flex items-center justify-center"><Radio className="w-4 h-4 text-white" /></div>
            <span className="font-heading text-xl font-black tracking-tighter text-white uppercase">AYO<span className="text-gray-500 ml-1 font-medium text-sm normal-case">source</span></span>
            <Badge className="bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20 font-mono text-[10px] ml-2">Brand</Badge>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="user-menu-button" variant="ghost" className="flex items-center gap-2 text-gray-300 hover:text-white hover:bg-white/5">
                <div className="w-7 h-7 rounded-full bg-[#7C3AED]/20 flex items-center justify-center"><User className="w-3.5 h-3.5 text-[#7C3AED]" /></div>
                <span className="text-sm font-medium">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-[#121212] border-white/10">
              <DropdownMenuLabel><p className="text-sm text-white font-medium">{user?.name}</p><p className="text-xs text-gray-500 font-mono">{user?.email}</p></DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="logout-button" className="text-red-400 focus:bg-red-500/10 cursor-pointer" onClick={logout}><LogOut className="w-4 h-4 mr-2" /> Sign Out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-white uppercase">Campaign HQ</h1>
            <p className="text-gray-500 text-sm mt-1">Manage your ad campaigns, creative, and placements.</p>
          </div>
          <div className="flex gap-3">
            <Button data-testid="upload-ad-button" onClick={() => navigate('/brand?tab=ads')} variant="ghost" className="text-gray-400 hover:text-white border border-white/10 hover:bg-white/5 text-sm">
              <Upload className="w-4 h-4 mr-2" /> Upload Ad
            </Button>
            <Button data-testid="new-campaign-button" onClick={() => navigate('/brand?tab=campaigns')} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-sm">
              <Plus className="w-4 h-4 mr-2" /> New Campaign
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Spend', value: `$${totalSpend.toFixed(2)}`, icon: DollarSign, color: '#7C3AED' },
            { label: 'Impressions', value: totalImpressions.toLocaleString(), icon: Eye, color: '#3B82F6' },
            { label: 'Active Campaigns', value: activeCampaigns, icon: Target, color: '#10B981' },
            { label: 'Approved Ads', value: approvedAds, icon: Film, color: '#F59E0B' },
            { label: 'Active Placements', value: approvedPlacements, icon: BarChart3, color: '#FF3B30' },
          ].map((s, i) => (
            <div key={i} className="border border-white/10 rounded-lg bg-[#121212]/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">{s.label}</span>
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
              </div>
              <p className="text-2xl font-bold text-white font-mono">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Campaigns + Onboarding */}
          <div className="lg:col-span-2 space-y-6">

            {/* Onboarding (show if not complete) */}
            {!onboardingComplete && (
              <div className="border border-[#7C3AED]/20 rounded-lg bg-[#7C3AED]/[0.02] p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-base font-bold text-white">Getting Started</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Complete these steps to launch your first campaign</p>
                  </div>
                  <span className="text-sm font-bold text-white font-mono">{completedSteps}/{steps.length}</span>
                </div>
                <Progress value={(completedSteps / steps.length) * 100} className="h-1.5 bg-white/5 mb-4" />
                <div className="space-y-2">
                  {steps.map((step, i) => (
                    <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded ${step.done ? 'bg-emerald-500/5' : 'bg-white/[0.02]'}`}>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-mono font-bold ${step.done ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-gray-500'}`}>
                        {step.done ? '✓' : i + 1}
                      </div>
                      <span className={`text-sm ${step.done ? 'text-gray-400 line-through' : 'text-white'}`}>{step.label}</span>
                    </div>
                  ))}
                </div>
                {completedSteps === 0 && (
                  <Button data-testid="start-first-upload" onClick={() => navigate('/brand?tab=ads')} className="w-full mt-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-sm">
                    <Upload className="w-4 h-4 mr-2" /> Upload Your First Ad
                  </Button>
                )}
              </div>
            )}

            {/* My Campaigns */}
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-base font-bold text-white">Campaigns</h3>
                <button onClick={() => navigate('/brand?tab=campaigns')} className="text-xs text-gray-500 hover:text-white font-mono flex items-center gap-1">View All <ChevronRight className="w-3 h-3" /></button>
              </div>
              {campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="w-8 h-8 mx-auto mb-2 text-gray-700" />
                  <p className="text-sm text-gray-500">No campaigns yet</p>
                  <p className="text-xs text-gray-600 mt-1">Create a campaign after uploading and getting an ad approved</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.slice(0, 4).map(c => (
                    <div key={c.id} data-testid={`campaign-${c.id}`} className="flex items-center justify-between p-4 border border-white/5 rounded-lg bg-white/[0.02] hover:border-white/10 transition-all cursor-pointer" onClick={() => navigate('/brand?tab=campaigns')}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-white font-medium truncate">{c.name}</p>
                          <StatusBadge status={c.status} />
                        </div>
                        <p className="text-xs text-gray-500 font-mono mt-1">{c.adFilename}</p>
                      </div>
                      <div className="flex items-center gap-6 flex-shrink-0 ml-4">
                        <div className="text-right">
                          <p className="text-sm font-bold text-white font-mono">${c.spent?.toFixed(2)}</p>
                          <p className="text-[10px] text-gray-600 font-mono">of ${c.budget?.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-white font-mono">{c.approvedPlacements || 0}</p>
                          <p className="text-[10px] text-gray-600 font-mono">placements</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* My Bids */}
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-base font-bold text-white">Recent Bids</h3>
                <button onClick={() => navigate('/brand?tab=bids')} className="text-xs text-gray-500 hover:text-white font-mono flex items-center gap-1">View All <ChevronRight className="w-3 h-3" /></button>
              </div>
              {placements.length === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-700" />
                  <p className="text-sm text-gray-500">No bids placed yet</p>
                  <p className="text-xs text-gray-600 mt-1">Browse the streamer marketplace to place your first bid</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {placements.slice(0, 5).map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 border border-white/5 rounded bg-white/[0.02]">
                      <div>
                        <p className="text-sm text-white">{p.streamerName}</p>
                        <p className="text-[10px] text-gray-600 font-mono">{p.adFilename} &middot; ${p.bidCpm?.toFixed(2)} CPM</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs font-mono text-white">{p.impressions} imp</p>
                          <p className="text-[10px] text-gray-600 font-mono">${p.spend?.toFixed(2)}</p>
                        </div>
                        <StatusBadge status={p.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Marketplace Preview + Quick Actions + Creatives */}
          <div className="space-y-6">

            {/* Quick Actions */}
            <div className="space-y-2">
              {[
                { label: 'Upload Ad Creative', desc: 'MP4, WebM, PNG, JPEG, GIF', icon: Upload, color: '#7C3AED', tab: 'ads' },
                { label: 'Browse Streamers', desc: 'Find audiences to target', icon: Users, color: '#3B82F6', tab: 'marketplace' },
                { label: 'Create Campaign', desc: 'Manual or programmatic', icon: Target, color: '#10B981', tab: 'campaigns' },
                { label: 'Programmatic', desc: 'AI-powered media buying', icon: Zap, color: '#FF3B30', tab: 'programmatic' },
              ].map((action, i) => (
                <button key={i} data-testid={`quick-${action.tab}`} onClick={() => navigate(action.tab === 'programmatic' ? '/brand/programmatic' : `/brand?tab=${action.tab}`)}
                  className="w-full flex items-center gap-4 p-4 border border-white/10 rounded-lg bg-[#121212]/50 hover:border-white/20 hover:bg-[#1E1E1E]/60 transition-all text-left group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${action.color}15` }}>
                    <action.icon className="w-5 h-5" style={{ color: action.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{action.label}</p>
                    <p className="text-xs text-gray-500">{action.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-300 transition-colors" />
                </button>
              ))}
            </div>

            {/* My Creatives Summary */}
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
              <h4 className="font-heading text-sm font-bold text-white uppercase mb-3">Creatives</h4>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { label: 'Pending', count: ads.filter(a => a.status === 'pending').length, color: 'text-gray-400' },
                  { label: 'Approved', count: approvedAds, color: 'text-emerald-400' },
                  { label: 'Rejected', count: ads.filter(a => a.status === 'rejected').length, color: 'text-red-400' },
                ].map((s, i) => (
                  <div key={i} className="text-center p-2 bg-white/[0.02] rounded border border-white/5">
                    <p className={`text-lg font-bold font-mono ${s.color}`}>{s.count}</p>
                    <p className="text-[9px] text-gray-600 font-mono uppercase">{s.label}</p>
                  </div>
                ))}
              </div>
              {ads.length > 0 && (
                <div className="space-y-1.5 mt-3">
                  {ads.slice(0, 3).map(ad => (
                    <div key={ad.id} className="flex items-center gap-2 text-xs">
                      <div className="w-8 h-5 bg-black rounded overflow-hidden flex-shrink-0">
                        {ad.mimeType?.startsWith('video/') ?
                          <video src={fullUrl(ad.url)} className="w-full h-full object-cover" muted preload="metadata" /> :
                          <img src={fullUrl(ad.url)} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <span className="text-gray-300 truncate flex-1">{ad.filename}</span>
                      <StatusBadge status={ad.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Streamers Preview */}
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-heading text-sm font-bold text-white uppercase">Top Streamers</h4>
                <button onClick={() => navigate('/brand?tab=marketplace')} className="text-[10px] text-gray-500 hover:text-white font-mono">Browse →</button>
              </div>
              {streamers.length === 0 ? (
                <p className="text-xs text-gray-600 text-center py-4">No streamers listed yet</p>
              ) : (
                <div className="space-y-2">
                  {streamers.slice(0, 4).map(s => (
                    <div key={s.id} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm text-white font-medium">{s.displayName || s.userName}</p>
                        <p className="text-[10px] text-gray-600 font-mono">{(s.avgCCV || 0).toLocaleString()} CCV</p>
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-mono text-[10px]">${s.floorCpm?.toFixed(2)}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Slot Info */}
            <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
              <h4 className="font-heading text-sm font-bold text-white uppercase mb-3">Ad Slots</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border border-white/5 rounded bg-white/[0.02] text-center">
                  <p className="text-xl font-bold text-white font-mono">15s</p>
                  <p className="text-[9px] text-gray-600 font-mono uppercase">Standard Slot</p>
                </div>
                <div className="p-3 border border-[#7C3AED]/20 rounded bg-[#7C3AED]/[0.02] text-center">
                  <p className="text-xl font-bold text-[#7C3AED] font-mono">30s</p>
                  <p className="text-[9px] text-gray-600 font-mono uppercase">Premium 1.8x</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
